import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'layout/atoms/Button/Button';
import FileInput from 'layout/atoms/FileInput';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import Textarea from 'layout/atoms/Textarea/Textarea';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import Grid from 'layout/molecules/Grid/Grid';
import parseImage from 'parsers/common/parseImage';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import styles from './ProductParameterEditor.module.scss';
import useProductParameterValidation from './useProductParameterValidation';
function ProductParameterEditor({ config, create = false }) {
    var _a;
    const dispatch = useDispatch();
    const { t } = useTranslation(['specification', 'common']);
    const modalName = create ? config.creatorModalName : config.editorModalName;
    const itemId = (_a = useSelector(selectModalByName(modalName))) === null || _a === void 0 ? void 0 : _a.id;
    const itemDetails = useSelector((state) => config.selectItemById(state, itemId || '')) || null;
    const status = useSelector(config.selectStatus);
    const [formValues, setFormValues] = useState({
        name: '',
        description: '',
        thumbnail: null,
    });
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const { errors, isValid, pendingFields, markTouched, validateAll } = useProductParameterValidation(formValues, t);
    useEffect(() => {
        if (!create && itemDetails) {
            setFormValues({
                name: itemDetails.name || '',
                description: itemDetails.description || '',
                thumbnail: itemDetails.thumbnail || null,
            });
        }
    }, [create, itemDetails, itemId]);
    useEffect(() => {
        if (formValues.thumbnail instanceof File) {
            const objectUrl = URL.createObjectURL(formValues.thumbnail);
            setThumbnailPreview(objectUrl);
            return () => URL.revokeObjectURL(objectUrl);
        }
        setThumbnailPreview(parseImage(formValues.thumbnail));
        return undefined;
    }, [formValues.thumbnail]);
    const handleFormChange = ({ target: { name, value }, }) => {
        setFormValues((values) => (Object.assign(Object.assign({}, values), { [name]: value })));
    };
    const handleThumbnailSelect = (selectedFile) => {
        const file = Array.isArray(selectedFile) ? selectedFile[0] : selectedFile;
        setFormValues((values) => (Object.assign(Object.assign({}, values), { thumbnail: file })));
    };
    const handleThumbnailDelete = () => {
        setFormValues((values) => (Object.assign(Object.assign({}, values), { thumbnail: null })));
    };
    const handleCancel = () => {
        dispatch(closeModal({ name: modalName }));
    };
    const handleFormSubmit = () => {
        if (!validateAll())
            return;
        const payload = {
            name: formValues.name.trim(),
            description: formValues.description.trim() || null,
            thumbnail: formValues.thumbnail,
        };
        if (create) {
            dispatch(config.addItem(payload));
        }
        else if (itemDetails) {
            dispatch(config.updateItem(Object.assign({ id: itemDetails.id }, payload)));
        }
        dispatch(closeModal({ name: modalName }));
    };
    const isSaving = status.add === 'loading' || status.update === 'loading';
    return (React.createElement(Modal, { size: "s", name: modalName, header: React.createElement(Label, { weight: "strong" }, create ? t(config.addLabelKey) : t(config.editLabelKey)), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: isSaving, pendingFields: pendingFields, onSave: handleFormSubmit, onCancel: handleCancel, validateAll: validateAll }), onClose: handleCancel },
        React.createElement(Grid, null,
            React.createElement(FormField, { label: t('common:name'), error: errors.name },
                React.createElement(Input, { value: formValues.name, onChange: handleFormChange, onBlur: () => markTouched('name'), name: "name", error: Boolean(errors.name), autoFocus: true })),
            React.createElement(FormField, { label: t('description') },
                React.createElement(Textarea, { value: formValues.description, onChange: handleFormChange, name: "description", rows: 4 })),
            React.createElement(FormField, { label: t('thumbnail') },
                React.createElement("div", { className: styles.thumbnailField },
                    React.createElement("div", { className: styles.preview }, thumbnailPreview ? (React.createElement("img", { src: thumbnailPreview, alt: formValues.name || t('thumbnail') })) : (t('no_image'))),
                    React.createElement("div", { className: styles.thumbnailActions },
                        React.createElement(FileInput, { allowedExtensions: ['jpg', 'jpeg', 'png', 'gif'], onSelect: handleThumbnailSelect }, (openFileChooser) => (React.createElement(Button, { size: "s", theme: "border", onClick: openFileChooser }, thumbnailPreview ? t('change_image') : t('add_image')))),
                        thumbnailPreview && (React.createElement(Button, { size: "s", theme: "ghost", color: "danger", onClick: handleThumbnailDelete }, t('remove_image')))))))));
}
export default memo(ProductParameterEditor);
